import {
  Anchor,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Slider,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getFunnel,
  getSearchProfiles,
  type FunnelObject,
  type FunnelProfileData,
} from '../lib/api.js';

const PROFILE_LABEL: Record<string, string> = {
  A_wohnung: 'Wohnung (Profil A)',
  B_buero: 'Büro (Profil B)',
};

const PROFILE_COLOR: Record<string, string> = {
  A_wohnung: 'violet',
  B_buero: 'cyan',
};

interface FunnelMetrics {
  total: number;
  killed: number;
  alive: number;
  overP1: number;
  reifOverP2: number;
  decided: number;
  hot: number;
  pass: number;
  pending: number;
  hotRate: number | null;
}

function computeMetrics(profile: FunnelProfileData, p1: number, p2: number): FunnelMetrics {
  const total = profile.total;
  const killed = profile.objects.filter((o) => o.killed).length;
  const alive = profile.objects.filter((o) => !o.killed);
  const overP1 = alive.filter(
    (o) => o.phase1Score !== null && o.phase1Score >= p1,
  );
  const reifOverP2 = overP1.filter(
    (o) => o.isReif && o.phase2Score !== null && o.phase2Score >= p2,
  );
  const decided = reifOverP2.filter((o) => o.verdict !== 'pending');
  const hot = decided.filter((o) => o.verdict === 'hot');
  const pass = decided.filter((o) => o.verdict === 'pass');
  const pending = reifOverP2.filter((o) => o.verdict === 'pending');
  return {
    total,
    killed,
    alive: alive.length,
    overP1: overP1.length,
    reifOverP2: reifOverP2.length,
    decided: decided.length,
    hot: hot.length,
    pass: pass.length,
    pending: pending.length,
    hotRate: decided.length > 0 ? (hot.length / decided.length) * 100 : null,
  };
}

export function FunnelTuning(): JSX.Element {
  const [p1, setP1] = useState(0.65);
  const [p2, setP2] = useState(0.65);
  const [touched, setTouched] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['funnel'],
    queryFn: getFunnel,
    refetchInterval: 60_000,
  });

  // Slider mit den echten Schwellen des ersten aktiven Such-Profils vorbelegen
  // (p1 = KI-Trigger, p2 = Swipe-Queue). Erst wenn der User selbst zieht, nicht mehr überschreiben.
  const { data: profilesData } = useQuery({ queryKey: ['search-profiles'], queryFn: getSearchProfiles });
  useEffect(() => {
    if (touched || !profilesData) return;
    const prof = profilesData.profiles.find((p) => p.active) ?? profilesData.profiles[0];
    if (prof) {
      setP1(prof.config.p1Threshold ?? 0.65);
      setP2(prof.config.p3Threshold ?? 0.65);
    }
  }, [profilesData, touched]);

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Anchor component={Link} to="/health" size="sm" c="dimmed">
          ← Übersicht
        </Anchor>
        <Title order={1}>Funnel-Tuning</Title>
        <Text c="dimmed" size="sm">
          Zieh an den Schwellen — die Kennzahlen werden rückwirkend aus deinen tatsächlichen
          Swipe-Entscheidungen neu berechnet.
        </Text>
      </Stack>

      <Card padding="lg">
        <Stack gap="lg">
          <Stack gap={6}>
            <Group justify="space-between" align="baseline">
              <Text fw={600}>Phase 1 — Schwelle für KI-Trigger</Text>
              <Text fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {p1.toFixed(2)}
              </Text>
            </Group>
            <Slider
              value={p1}
              onChange={(v) => {
                setP1(v);
                setTouched(true);
              }}
              min={0.4}
              max={0.85}
              step={0.01}
              marks={[
                { value: 0.5, label: '0.50' },
                { value: 0.65, label: '0.65' },
                { value: 0.75, label: '0.75' },
                { value: 0.8, label: '0.80' },
              ]}
              label={(v) => v.toFixed(2)}
              color="violet"
            />
          </Stack>

          <Stack gap={6} mt="lg">
            <Group justify="space-between" align="baseline">
              <Text fw={600}>Phase 2 — Schwelle für Swipe-Queue</Text>
              <Text fw={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {p2.toFixed(2)}
              </Text>
            </Group>
            <Slider
              value={p2}
              onChange={(v) => {
                setP2(v);
                setTouched(true);
              }}
              min={0.4}
              max={0.85}
              step={0.01}
              marks={[
                { value: 0.5, label: '0.50' },
                { value: 0.65, label: '0.65' },
                { value: 0.75, label: '0.75' },
                { value: 0.8, label: '0.80' },
              ]}
              label={(v) => v.toFixed(2)}
              color="teal"
            />
          </Stack>

          <Text size="xs" c="dimmed">
            <b>Hot-Rate</b> = %-Anteil deiner „Hot"-Entscheidungen unter den Objekten, die diese
            Schwellen passiert hätten <i>und</i> die du tatsächlich entschieden hast. Tief = Schwellen
            zu lax (zu viel Schrott durchgewunken). Hoch = Schwellen treffsicher. Aussagekraft
            wächst mit der Anzahl entschiedener Objekte.
          </Text>
        </Stack>
      </Card>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {data && (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {Object.entries(data.profiles).map(([key, prof]) => (
            <ProfileCard key={key} profileKey={key} prof={prof} p1={p1} p2={p2} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}

function ProfileCard({
  profileKey,
  prof,
  p1,
  p2,
}: {
  profileKey: string;
  prof: FunnelProfileData;
  p1: number;
  p2: number;
}): JSX.Element {
  const m = computeMetrics(prof, p1, p2);
  const color = PROFILE_COLOR[profileKey] ?? 'gray';

  return (
    <Card padding="lg" style={{ borderTop: `4px solid var(--mantine-color-${color}-6)` }}>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={3}>{PROFILE_LABEL[profileKey] ?? profileKey}</Title>
          <Badge color={color} variant="light" size="lg" tt="none">
            {m.total} Inserate
          </Badge>
        </Group>

        <Stack gap={6}>
          <FunnelRow
            label="lebendig (nicht gekillt)"
            value={m.alive}
            of={m.total}
            color="gray"
          />
          <FunnelRow
            label={`über P1-Schwelle (≥ ${p1.toFixed(2)})`}
            value={m.overP1}
            of={m.alive}
            color="violet"
          />
          <FunnelRow
            label={`reif & über P2-Schwelle (≥ ${p2.toFixed(2)})`}
            value={m.reifOverP2}
            of={m.overP1}
            color="teal"
          />
          <FunnelRow
            label="davon entschieden"
            value={m.decided}
            of={m.reifOverP2}
            color="indigo"
          />
          <FunnelRow
            label="↳ Hot"
            value={m.hot}
            of={m.decided}
            color="green"
            indent
          />
          <FunnelRow
            label="↳ Pass"
            value={m.pass}
            of={m.decided}
            color="red"
            indent
          />
          <FunnelRow
            label="↳ noch pending"
            value={m.pending}
            of={m.reifOverP2}
            color="gray"
            indent
          />
        </Stack>

        <Card
          padding="md"
          style={{
            background:
              m.hotRate === null
                ? 'rgba(148,163,184,0.10)'
                : m.hotRate >= 70
                  ? 'rgba(16,185,129,0.12)'
                  : m.hotRate >= 40
                    ? 'rgba(234,179,8,0.12)'
                    : 'rgba(239,68,68,0.12)',
          }}
        >
          {m.hotRate === null ? (
            <Text size="sm" c="dimmed">
              Noch keine Entscheidungen bei diesen Schwellen — Hot-Rate nicht aussagekräftig.
            </Text>
          ) : (
            <Stack gap={4}>
              <Group justify="space-between" align="baseline">
                <Text fw={700} size="md">
                  Hot-Rate bei P1≥{p1.toFixed(2)} / P2≥{p2.toFixed(2)}
                </Text>
                <Text
                  fw={800}
                  style={{ fontSize: '1.5rem', fontVariantNumeric: 'tabular-nums' }}
                >
                  {m.hotRate.toFixed(0)}%
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {m.hot} von {m.decided} entschiedenen waren Hot. {m.pending > 0 && (
                  <>({m.pending} noch ausstehend.)</>
                )}
              </Text>
            </Stack>
          )}
        </Card>
      </Stack>
    </Card>
  );
}

function FunnelRow({
  label,
  value,
  of,
  color,
  indent,
}: {
  label: string;
  value: number;
  of?: number;
  color: string;
  indent?: boolean;
}): JSX.Element {
  const pct = of && of > 0 ? (value / of) * 100 : null;
  return (
    <Stack gap={2}>
      <Group justify="space-between" gap="xs">
        <Text size="sm" style={{ paddingLeft: indent ? 12 : 0 }}>
          {label}
        </Text>
        <Group gap={6}>
          <Text fw={600} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Text>
          {pct !== null && (
            <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums', minWidth: 50 }}>
              ({pct.toFixed(0)}%)
            </Text>
          )}
        </Group>
      </Group>
      {pct !== null && (
        <Progress
          value={pct}
          color={color}
          size="xs"
          radius="sm"
          style={{ marginLeft: indent ? 12 : 0 }}
        />
      )}
    </Stack>
  );
}
